<template>
  <header class="relative px-4 lg:px-8">
    <nav class="mx-auto h-full min-h-11 w-full max-w-5xl space-y-4 pb-2 pt-6">
      <StatusBanner v-if="hasUpdate" variant="success" :show-icon="false">
        <span>
          <ArrowUpRight v-if="isInternal" class="inline-icon" />
          <ArrowBigUpDashIcon v-else class="inline-icon" />
          {{ t('newVersionAvailable' + (isInternal ? 'Internal' : '')) }}
        </span>
        <template #actions>
          <CVButton variant="link" @click="reloadApp">
            <RotateCwIcon /> {{ t('reloadNow' + (isInternal ? 'Internal' : '')) }}
          </CVButton>
        </template>
      </StatusBanner>
      <div class="flex items-center justify-between">
        <div
          v-if="uiStore.headerTitle"
          class="flex flex-col gap-1 py-1 md:flex-row md:items-center md:gap-4"
        >
          <h1>
            {{ uiStore.headerTitle }}
          </h1>

          <div
            v-if="uiStore.subTitleLine1"
            class="flex flex-col border-secondary md:border-l-2 md:pl-4"
          >
            <p class="font-medium leading-4">{{ uiStore.subTitleLine1 }}</p>
            <p class="font-normal">{{ uiStore.subTitleLine2 }}</p>
          </div>
        </div>
        <div v-else />

        <div class="ml-2 flex items-center gap-2 md:gap-4">
          <div
            v-if="isStaging"
            class="mr-10 rounded-full bg-red-100 px-3 py-1 text-white max-sm:hidden"
          >
            {{ t('testEnvironment') }}
          </div>
          <PhoneOverlay v-if="me.data.value?.sipId" />
          <ClockInOut v-if="isTimeTrackingEnabled" />
          <div class="mt-1 hidden flex-grow justify-end md:flex">
            <img src="@/assets/carvia_logo.svg" class="max-w-[130px] lg:max-w-[140px]" />
          </div>
          <NovuNotifications />
          <LangSwitcher />

          <label class="relative outline-none" tabindex="0">
            <HeaderBarHamburger v-model:isActive="isActive" />
            <HeaderbarDropdown :class="{ hidden: !isActive }" />
          </label>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { useUiStore } from '@/stores/ui.store';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import HeaderbarDropdown from './components/HeaderbarDropdown.vue';
import HeaderBarHamburger from './components/HeaderBarHamburger.vue';
import LangSwitcher from './components/LangSwitcher.vue';
import ClockInOut from './ClockInOut.vue';
import { useI18n } from 'vue-i18n';
import { versionService } from '@/services/version.service';
import CVButton from '../buttons/CVButton.vue';
import StatusBanner from '../StatusBanner.vue';
import { ArrowBigUpDashIcon, ArrowUpRight, RotateCwIcon } from 'lucide-vue-next';
import { useMe } from '@/queries/use-users';
import NovuNotifications from '../NovuNotifications.vue';
import PhoneOverlay from '../PhoneOverlay.vue';
import { useFeatureFlag } from '@/hooks/use-feature-flag';

const uiStore = useUiStore();
const { t } = useI18n();
const isStaging = import.meta.env.VITE_ENVIRONMENT === 'staging';
const isActive = ref(false);
const hasUpdate = ref(false);
let checkInterval: number;
const me = useMe();

const internalUsers = [
  'joschka.reik@carvia.de',
  'julian.reik@carvia.de',
  'johannes.spohr@carvia.de',
  'luigi.junge@carvia.de',
  'sebastian.hueftlein@carvia.de',
];

const isInternal = computed(() => {
  return me.data.value?.email && internalUsers.includes(me.data.value.email);
});

const isTimeTrackingEnabled = useFeatureFlag('time-tracking');

onMounted(() => {
  checkForUpdates();
  checkInterval = window.setInterval(checkForUpdates, 10 * 1000);
});

onUnmounted(() => {
  clearInterval(checkInterval);
});

async function checkForUpdates() {
  hasUpdate.value = await versionService.checkForUpdates();
}

function reloadApp() {
  window.location.reload();
}
</script>

<i18n lang="json">
{
  "en": {
    "testEnvironment": "Test environment",
    "newVersionAvailable": "A new version is available",
    "newVersionAvailableInternal": "ROS just got even sicker",
    "reloadNow": "Reload now",
    "reloadNowInternal": "Give it to me"
  },
  "de": {
    "testEnvironment": "Testumgebung",
    "newVersionAvailable": "Es ist eine neue Version verfügbar",
    "newVersionAvailableInternal": "ROS wurde eben noch geiler",
    "reloadNow": "Jetzt neu laden",
    "reloadNowInternal": "Gib's mir"
  },
  "es": {
    "testEnvironment": "Entorno de pruebas",
    "newVersionAvailable": "Hay una nueva versión disponible",
    "newVersionAvailableInternal": "ROS acaba de ponerse aún mejor",
    "reloadNow": "Recargar ahora",
    "reloadNowInternal": "Dámela"
  }
}
</i18n>
